import React, { useEffect, useState, useRef } from 'react';
import { RichTextEditor } from './RichTextEditor';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { User } from '../../types';
import { Send, AtSign, X } from 'lucide-react';
interface NoteComposerProps {
  onSave: (note: {
    subject: string;
    content: string;
    isPrivate: boolean;
    category: string;
    taggedUsers: User[];
  }) => void;
  onCancel?: () => void;
  defaultExpanded?: boolean;
  availableUsers?: User[];
}
const CATEGORIES = ['General', 'Underwriting', 'Processing', 'Closing', 'Call', 'Email'];
export function NoteComposer({
  onSave,
  onCancel,
  defaultExpanded = false,
  availableUsers = []
}: NoteComposerProps) {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('General');
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [taggedUsers, setTaggedUsers] = useState<User[]>([]);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const tagPickerRef = useRef<HTMLDivElement>(null);
  const tagSearchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!tagPickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (tagPickerRef.current && !tagPickerRef.current.contains(e.target as Node)) {
        setTagPickerOpen(false);
        setTagSearch('');
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setTagPickerOpen(false);
        setTagSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [tagPickerOpen]);
  useEffect(() => {
    if (tagPickerOpen && tagSearchRef.current) {
      tagSearchRef.current.focus();
    }
  }, [tagPickerOpen]);
  const toggleUser = (user: User) => {
    setTaggedUsers(prev => prev.some(u => u.id === user.id) ? prev.filter(u => u.id !== user.id) : [...prev, user]);
  };
  const removeTaggedUser = (userId: string) => {
    setTaggedUsers(prev => prev.filter(u => u.id !== userId));
  };
  const filteredAvailableUsers = availableUsers.filter(u => u.name.toLowerCase().includes(tagSearch.toLowerCase()) || u.role.toLowerCase().includes(tagSearch.toLowerCase()));
  const handleSubmit = () => {
    if (!subject.trim() || !content.trim()) return;
    onSave({
      subject,
      content,
      isPrivate: false,
      category,
      taggedUsers
    });
    setSubject('');
    setContent('');
    setTaggedUsers([]);
    setIsExpanded(defaultExpanded);
  };
  const handleCancel = () => {
    setSubject('');
    setContent('');
    setTaggedUsers([]);
    setIsExpanded(defaultExpanded);
    onCancel?.();
  };
  return <div className="space-y-4" data-id="element-77">
      {/* Top row: subject + category */}
      <div className="flex gap-3 items-start" data-id="element-78">
        <div className="flex-1" data-id="element-79">
          <Input placeholder="Subject *" value={subject} onChange={e => {
          setSubject(e.target.value);
          if (!isExpanded) setIsExpanded(true);
        }} className="font-medium" data-id="element-80" />
        </div>
        <div className="shrink-0" data-id="element-81">
          <select value={category} onChange={e => setCategory(e.target.value)} className="h-10 text-sm border border-slate-300 bg-white rounded-md px-3 text-slate-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer" data-id="element-82">
            {CATEGORIES.map(c => <option key={c} value={c} data-id="element-83">
                {c}
              </option>)}
          </select>
        </div>
      </div>

      {/* Tagged Users Pills */}
      {taggedUsers.length > 0 && <div className="flex flex-wrap gap-1.5" data-id="element-84">
          {taggedUsers.map(user => <span key={user.id} className="inline-flex items-center gap-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded-full" data-id="element-85">
              @{user.name}
              <button type="button" onClick={() => removeTaggedUser(user.id)} className="text-blue-400 hover:text-blue-600 transition-colors" data-id="element-86">
                <X className="h-3 w-3" data-id="element-87" />
              </button>
            </span>)}
        </div>}

      {/* Rich text editor — always shown when expanded */}
      {isExpanded && <>
          <RichTextEditor value={content} onChange={setContent} placeholder="Write your note here..." minHeight="160px" data-id="element-88" />

          {/* Footer actions */}
          <div className="flex items-center justify-between pt-1" data-id="element-89">
            <div className="flex items-center gap-3" data-id="element-90">
              {/* Tag Users Button */}
              <div className="relative" ref={tagPickerRef} data-id="element-91">
                <button type="button" onClick={() => setTagPickerOpen(!tagPickerOpen)} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors ${taggedUsers.length > 0 ? 'text-blue-700 bg-blue-50 border border-blue-200' : 'text-slate-500 hover:bg-slate-100 border border-transparent'}`} data-id="element-92">
                  <AtSign className="h-3.5 w-3.5" data-id="element-93" />
                  Tag{taggedUsers.length > 0 ? ` (${taggedUsers.length})` : ''}
                </button>

                {tagPickerOpen && <div className="absolute left-0 bottom-full mb-1 w-64 bg-white rounded-lg border border-slate-200 shadow-lg z-30 animate-in fade-in slide-in-from-bottom-2 duration-150" data-id="element-94">
                    <div className="p-2 border-b border-slate-100" data-id="element-95">
                      <input ref={tagSearchRef} type="text" placeholder="Search users..." value={tagSearch} onChange={e => setTagSearch(e.target.value)} className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-slate-400" data-id="element-96" />
                    </div>
                    <div className="max-h-48 overflow-y-auto py-1" data-id="element-97">
                      {filteredAvailableUsers.length === 0 ? <p className="text-xs text-slate-400 text-center py-3" data-id="element-98">
                          No users found
                        </p> : filteredAvailableUsers.map(user => {
                  const isSelected = taggedUsers.some(u => u.id === user.id);
                  return <button key={user.id} type="button" onClick={() => toggleUser(user)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`} data-id="element-99">
                              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${isSelected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-100">
                                {user.initials}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-101">
                                <p className="text-xs font-medium text-slate-800 truncate" data-id="element-102">
                                  {user.name}
                                </p>
                                <p className="text-[10px] text-slate-400 truncate" data-id="element-103">
                                  {user.role}
                                </p>
                              </div>
                              {isSelected && <div className="h-4 w-4 rounded-full bg-blue-600 flex items-center justify-center shrink-0" data-id="element-104">
                                  <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-105">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-106" />
                                  </svg>
                                </div>}
                            </button>;
                })}
                    </div>
                  </div>}
              </div>
            </div>

            <div className="flex items-center gap-2" data-id="element-107">
              <Button variant="ghost" size="sm" onClick={handleCancel} data-id="element-108">
                Cancel
              </Button>
              <Button size="sm" onClick={handleSubmit} disabled={!subject.trim() || !content.trim()} data-id="element-109">
                <Send className="h-3.5 w-3.5 mr-1.5" data-id="element-110" />
                Post Note
              </Button>
            </div>
          </div>
        </>}
    </div>;
}